import React, { useMemo } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { QuestionAnswer as QuestionIcon } from "@mui/icons-material";
import { matchDailyOptionalQuestions } from "../../utils/dailyOptionalQuestionsUtils";

/**
 * Renders answered daily check-in questions for a single day (reportHealth.reportData.optional).
 */
export default function DailyOptionalQuestionsSection({
  optionalResponses,
  organizationOptionals,
  loading = false,
  title = "Daily check-in",
  showHeader = true,
  compact = false,
}) {
  const matchedQuestions = useMemo(
    () => matchDailyOptionalQuestions(optionalResponses, organizationOptionals),
    [optionalResponses, organizationOptionals],
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: compact ? 1 : 2 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (!matchedQuestions.length) {
    return null;
  }

  return (
    <Box>
      {showHeader ? (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: compact ? 1 : 1.5 }}>
          <QuestionIcon color="secondary" fontSize="small" sx={{ color: "deepPurple.main" }} />
          <Typography variant={compact ? "subtitle2" : "subtitle1"} fontWeight="bold">
            {title}
          </Typography>
        </Stack>
      ) : null}

      <Stack spacing={compact ? 1 : 1.5} divider={compact ? undefined : <Divider flexItem />}>
        {matchedQuestions.map((question) => (
          <Paper
            key={question.id}
            variant="outlined"
            sx={{
              p: compact ? 1.25 : 1.5,
              borderRadius: 1.5,
              bgcolor: "background.paper",
            }}
          >
            <Typography variant="body2" fontWeight={600} gutterBottom>
              {question.name}
            </Typography>
            {question.subTitle ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                {question.subTitle}
              </Typography>
            ) : null}
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ whiteSpace: "pre-wrap" }}
            >
              {question.displayValue}
            </Typography>
            {question.notes ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                Note: {question.notes}
              </Typography>
            ) : null}
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}
